import { io } from "socket.io-client";
import { toast } from "sonner";
import { postSocketAuth } from "./api/endpoints";
import { t } from "./lib/dictionary";
import type { AimProgress } from "@globals/foscam/types";
import { useBmsStore } from "./store/bms";
import { useChartStore } from "./store/chart";
import { useLayoutStore } from "./store/layout";
import { useLayoutEditor } from "./store/layoutEditor";
import { useMetricMetaStore } from "./store/metricMeta";
import { usePipStore } from "./store/pip";
import { useStatsStore } from "./store/stats";
import { useSysmonStore } from "./store/sysmon";
import { useSystemStore } from "./store/system";
import { clearHistory, pushSnapshot } from "./lib/sysmon/history";
import { useTracerStore } from "./store/tracer";
import { useUserStore } from "./store/user";
import { useFoscamStore } from "./store/foscam";
import { useValuesStore } from "./store/values";
import type {
  BmsOverviewDiffWsEvent,
  BmsOverviewFullWsEvent,
  BmsStateWsEvent,
  BmsTelemetryDiffWsEvent,
  BmsTelemetryFullWsEvent,
} from "./types/bms";
import type {
  PipOverviewDiffWsEvent,
  PipOverviewFullWsEvent,
  PipStateWsEvent,
  PipTelemetryDiffWsEvent,
  PipTelemetryFullWsEvent,
} from "./types/pip";
import type {
  TracerOverviewDiffWsEvent,
  TracerOverviewFullWsEvent,
  TracerStateWsEvent,
  TracerTelemetryDiffWsEvent,
  TracerTelemetryFullWsEvent,
} from "./types/tracer";
import type {
  SysmonHostStateWsEvent,
  SysmonHostsWsEvent,
  SysmonProcWsEvent,
  SysmonSnapshotDiffWsEvent,
  SysmonSnapshotFullWsEvent,
} from "./types/sysmon";
import { flushSubscription } from "./lib/wsSubscriptions";
import { API_URL } from "./utils/apiUrl";

export const socket = io(API_URL, {
  autoConnect: false,
  withCredentials: true,
  tryAllTransports: false,
  transports: ["websocket"],
  reconnection: true,
  upgrade: true,
  reconnectionDelayMax: 30_000,
});

// ===== Connection lifecycle → systemStore =====

socket.on("connect", async () => {
  useSystemStore.getState().setWsStatus("connected");
  if (socket.id) {
    try {
      await postSocketAuth({ socketId: socket.id });
      // Po auth pošli aktuální subscription (po reconnect server vše zapomněl,
      // pokud nešlo o connection state recovery). Server odpoví initial dumpem
      // MQTT values + full snapshotem device telemetry pro subscribed slugy.
      flushSubscription();
      // Layouty se po reconnectu dotáhnou samy — jinak by aplikace, která nabootovala
      // během restartu serveru, zůstala na chybové hlášce až do F5.
      if (useLayoutStore.getState().status !== "ready") void useLayoutStore.getState().load();
    } catch {
      // Auth fail — socket zůstane připojený, ale neprodá se mu "auth" room
      useSystemStore.getState().setWsStatus("error");
    }
  }
});

socket.on("disconnect", () => {
  useSystemStore.getState().setWsStatus("disconnected");
});

socket.on("connect_error", () => {
  useSystemStore.getState().setWsStatus("error");
});

// ===== Data events → valuesStore + systemStore =====

socket.on("values", (vals: MQTTValueTransfer[]) => {
  useValuesStore.getState().applyWsBatch(vals);
  // Drž metricMeta cache aktuální — kdyby admin změnil nickname/unit za běhu,
  // příští WS zpráva to propaguje. Skip když metadata nepřišla (server vždy posílá).
  const upsert = useMetricMetaStore.getState().upsert;
  for (const v of vals) {
    if (v.topic) upsert(v.topic);
  }
});

// "Hodnota se nezměnila, ale stále teče" — server pošle jen seznam topicIds + společný
// timestamp. Klient vytvoří nové reference v store → Dot animace pulsne, value zůstává.
// Sparkline historie se nedotčená (heartbeat je čistě UI-effect). Viz docs/plans/WS-TRAFFIC-REDUCTION.md.
socket.on("values:heartbeat", (hb: WsValuesHeartbeat) => {
  useValuesStore.getState().applyHeartbeat(hb.topicIds, hb.ts);
});

// ===== Systémový monitor (btop-like) =====
// Handlery zapisují na DVĚ místa: snapshot do Zustandu (readouty) a vzorky do ring
// bufferů v lib/sysmon/history.ts (grafy). Historie záměrně NENÍ ve storu — mutace
// Float32Array by nefire-ovala selektor a kopírovat pole každý tik je anti-vzor.

socket.on("sysmon:hosts", (ev: SysmonHostsWsEvent) => {
  useSysmonStore.getState().setHosts(ev.hosts);
});

socket.on("sysmon:snapshot:full", (ev: SysmonSnapshotFullWsEvent) => {
  pushSnapshot(ev.snapshot);
  useSysmonStore.getState().applySnapshotFull(ev.hostId, ev.snapshot);
});

socket.on("sysmon:snapshot:diff", (ev: SysmonSnapshotDiffWsEvent) => {
  const store = useSysmonStore.getState();
  store.applySnapshotDiff(ev.hostId, ev.patch);
  // Do historie jde až SLOŽENÝ snapshot — patch sám o sobě nemusí obsahovat sekci,
  // ze které se daná řada bere.
  const merged = useSysmonStore.getState().byHost.get(ev.hostId)?.snapshot;
  if (merged) pushSnapshot(merged);
});

socket.on("sysmon:proc", (ev: SysmonProcWsEvent) => {
  useSysmonStore.getState().applyProcesses(ev);
});

socket.on("sysmon:host:state", (ev: SysmonHostStateWsEvent) => {
  useSysmonStore.getState().applyHostState(ev);
});

/**
 * Globální layout změněn (admin uložil) → přenačti daný klíč (projeví se všem klientům).
 *
 * Když má někdo zrovna otevřený editor téže plochy, rozhoduje se podle toho, jestli má
 * rozdělanou práci: čistý draft se tiše srovná na novou verzi, rozdělaný se NECHÁ BÝT
 * a jen označí konflikt. Kdyby se přepsal, přišel by o práci; kdyby se nechal být bez
 * označení, jeho uložení by cizí změnu tiše přemazalo.
 */
socket.on("layouts:changed", (ev: { key: string; version: number }) => {
  void (async () => {
    await useLayoutStore.getState().refetchOne(ev.key);
    const ed = useLayoutEditor.getState();
    if (ed.draftKey !== ev.key) return;
    const fresh = useLayoutStore.getState().byKey.get(ev.key);
    if (!fresh) return;
    if (ed.dirty) ed.noteExternalChange(fresh.version);
    else ed.rebase(fresh.name, fresh.doc);
  })();
});

// ===== BMS events =====
// Diff/full pattern: full snapshot při subscribe a periodicky (30s resync), diff každý tick
// mezi tím. Overview = "lite" subset pro DevicesPage karty. Viz docs/plans/WS-TRAFFIC-REDUCTION.md.

socket.on("bms:telemetry:full", (ev: BmsTelemetryFullWsEvent) => {
  useBmsStore.getState().applyTelemetryFull(ev.slug, ev.telemetry);
});

socket.on("bms:telemetry:diff", (ev: BmsTelemetryDiffWsEvent) => {
  useBmsStore.getState().applyTelemetryDiff(ev.slug, ev.patch);
});

socket.on("bms:overview:full", (ev: BmsOverviewFullWsEvent) => {
  useBmsStore.getState().applyOverviewFull(ev.slug, ev.telemetry);
});

socket.on("bms:overview:diff", (ev: BmsOverviewDiffWsEvent) => {
  useBmsStore.getState().applyOverviewDiff(ev.slug, ev.patch);
});

socket.on("bms:state", (ev: BmsStateWsEvent) => {
  useBmsStore.getState().applyState(ev);
});

// ===== PIP events =====

socket.on("pip:telemetry:full", (ev: PipTelemetryFullWsEvent) => {
  usePipStore.getState().applyTelemetryFull(ev.slug, ev.telemetry);
});

socket.on("pip:telemetry:diff", (ev: PipTelemetryDiffWsEvent) => {
  usePipStore.getState().applyTelemetryDiff(ev.slug, ev.patch);
});

socket.on("pip:overview:full", (ev: PipOverviewFullWsEvent) => {
  usePipStore.getState().applyOverviewFull(ev.slug, ev.telemetry);
});

socket.on("pip:overview:diff", (ev: PipOverviewDiffWsEvent) => {
  usePipStore.getState().applyOverviewDiff(ev.slug, ev.patch);
});

socket.on("pip:state", (ev: PipStateWsEvent) => {
  usePipStore.getState().applyState(ev);
});

// ===== Tracer events =====

socket.on("tracer:telemetry:full", (ev: TracerTelemetryFullWsEvent) => {
  useTracerStore.getState().applyTelemetryFull(ev.slug, ev.telemetry);
});

socket.on("tracer:telemetry:diff", (ev: TracerTelemetryDiffWsEvent) => {
  useTracerStore.getState().applyTelemetryDiff(ev.slug, ev.patch);
});

socket.on("tracer:overview:full", (ev: TracerOverviewFullWsEvent) => {
  useTracerStore.getState().applyOverviewFull(ev.slug, ev.telemetry);
});

socket.on("tracer:overview:diff", (ev: TracerOverviewDiffWsEvent) => {
  useTracerStore.getState().applyOverviewDiff(ev.slug, ev.patch);
});

socket.on("tracer:state", (ev: TracerStateWsEvent) => {
  useTracerStore.getState().applyState(ev);
});

// ===== Foscam events =====
// Nízkofrekvenční broadcast schopností kamer (změna kodeku / presetů) do room "auth".

socket.on("foscam:caps", (caps: FoscamCaps[]) => {
  useFoscamStore.getState().setCaps(caps);
});

// Průběh míření (otočení kamery na dvojklik). Chodí všem přihlášeným, protože kamera
// se fyzicky hýbe i těm, kdo míření nespustili — ať se nediví, proč obraz ujíždí.
socket.on("foscam:aim", (progress: AimProgress) => {
  useFoscamStore.getState().applyAimProgress(progress);
});

// Kameru se nepodařilo zastavit. Trvalý alarm pro VŠECHNY — pohybující se hardware,
// který neposlechl stop, je sdílený problém, ne problém toho, kdo klikl.
socket.on("foscam:aim:stopFailed", ({ slug }: { slug: string }) => {
  toast.error(`${t.foscam.stopFailed} (${slug})`, { duration: Infinity, id: `ptz-stop-${slug}` });
});

// ===== Auto-connect na základě user state + network state =====

let userWantsConnection = false;

function syncConnection() {
  if (!userWantsConnection) {
    if (socket.active || socket.connected) socket.disconnect();
    return;
  }
  if (navigator.onLine) {
    if (!socket.active && !socket.connected) {
      useSystemStore.getState().setWsStatus("connecting");
      socket.connect();
    }
  } else if (socket.active || socket.connected) {
    // Bez netu zastav reconnect smyčku, jinak browser spamuje WS errory.
    socket.disconnect();
    useSystemStore.getState().setWsStatus("disconnected");
  }
}

useUserStore.subscribe((state) => {
  const isLoggedIn = state.user !== null && state.user !== false;
  if (isLoggedIn === userWantsConnection) return;
  userWantsConnection = isLoggedIn;
  if (!isLoggedIn) {
    useValuesStore.getState().clear();
    useMetricMetaStore.getState().clear();
    useLayoutStore.getState().clear();
    useBmsStore.getState().clear();
    usePipStore.getState().clear();
    useTracerStore.getState().clear();
    useChartStore.getState().clear();
    useStatsStore.getState().clear();
    useSysmonStore.getState().clear();
    // Ring buffery grafů žijí mimo store, takže je nutné je uklidit zvlášť —
    // jinak by po odhlášení a přihlášení jiného uživatele zůstala cizí historie.
    clearHistory();
  }
  syncConnection();
});

// Při návratu na tab resetuj reconnect backoff (jinak může čekat až 30 s
// na další naplánovaný pokus). Zombie spojení (connected v klientovi,
// odpojené na serveru) si vyřeší socket.io ping timeout.
function kickReconnect() {
  if (!userWantsConnection || !navigator.onLine) return;
  if (socket.connected) return;
  if (socket.active) socket.disconnect();
  useSystemStore.getState().setWsStatus("connecting");
  socket.connect();
}

if (typeof window !== "undefined") {
  window.addEventListener("online", syncConnection);
  window.addEventListener("offline", syncConnection);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") kickReconnect();
  });
}
